import { useState, useEffect } from 'react';
import { Users, UserCheck, UserX, HelpCircle, Trash2, LogOut, RefreshCw, Image as ImageIcon, QrCode, CalendarDays, Heart, Plane, Clock, MapPin, Sparkles, ClipboardList, Info, Crown, Home, Search, X, Feather, UserPlus } from 'lucide-react';
import axios from 'axios';
import AdminGalleryManager from './AdminGalleryManager';
import AdminGuestBookManager from './AdminGuestBookManager';
import AdminWeddingQR from './AdminWeddingQR';
import AdminEventContentManager from './AdminEventContentManager';
import AdminOurStoryManager from './AdminOurStoryManager';
import AdminTravelManager from './AdminTravelManager';
import AdminScheduleManager from './AdminScheduleManager';
import AdminEventDayManager from './AdminEventDayManager';
import AdminSaveTheDate from './AdminSaveTheDate';
import AdminHeroManager from './AdminHeroManager';
import AdminMonogramLibrary from './AdminMonogramLibrary';
import AdminGuestListManager from './AdminGuestListManager';
import AdminRSVPSettings from './AdminRSVPSettings';
import AdminMenuManager from './AdminMenuManager';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [authError, setAuthError] = useState('');
  const [rsvps, setRsvps] = useState([]);
  const [rsvpSearch, setRsvpSearch] = useState('');
  const [guestbookCount, setGuestbookCount] = useState(0);
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState('rsvps');
  const [loading, setLoading] = useState(false);

  const getAuthHeader = () => {
    const encoded = btoa(`${credentials.username}:${credentials.password}`);
    return { Authorization: `Basic ${encoded}` };
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthError('');
    setLoading(true);
    
    try {
      const encoded = btoa(`${credentials.username}:${credentials.password}`);
      await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Basic ${encoded}` }
      });
      setIsAuthenticated(true);
      fetchData();
    } catch (error) {
      setAuthError('Invalid username or password');
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [rsvpRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/rsvps`, { headers: getAuthHeader() }),
        axios.get(`${API}/admin/stats`, { headers: getAuthHeader() }),
      ]);
      setRsvps(rsvpRes.data);
      setStats(statsRes.data);
      setGuestbookCount(statsRes.data?.guestbook_entries || 0);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRsvp = async (rsvpId) => {
    if (!window.confirm('Are you sure you want to delete this RSVP?')) return;
    
    try {
      await axios.delete(`${API}/admin/rsvp/${rsvpId}`, { headers: getAuthHeader() });
      fetchData();
    } catch (error) {
      alert('Failed to delete RSVP');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setCredentials({ username: '', password: '' });
    setRsvps([]);
    setStats(null);
    setGuestbookCount(0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#F8F5EF] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
          <h1 className="font-serif text-3xl text-[#3B2F2F] text-center mb-2">Admin Login</h1>
          <p className="text-[#5A4A4A] text-center mb-8">Mary & Arrington Wedding</p>
          
          {authError && (
            <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm">
              {authError}
            </div>
          )}
          
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-[#3B2F2F] text-sm font-medium mb-2">Username</label>
              <input
                type="text"
                value={credentials.username}
                onChange={(e) => setCredentials(prev => ({ ...prev, username: e.target.value }))}
                className="w-full border border-[#E5E0D8] rounded-lg p-3 focus:outline-none focus:border-[#C8A951]"
                placeholder="admin"
                data-testid="admin-username-input"
                required
              />
            </div>
            <div>
              <label className="block text-[#3B2F2F] text-sm font-medium mb-2">Password</label>
              <input
                type="password"
                value={credentials.password}
                onChange={(e) => setCredentials(prev => ({ ...prev, password: e.target.value }))}
                className="w-full border border-[#E5E0D8] rounded-lg p-3 focus:outline-none focus:border-[#C8A951]"
                placeholder="••••••••"
                data-testid="admin-password-input"
                required
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#C8A951] text-white py-3 rounded-full font-medium hover:bg-[#B09240] transition-colors disabled:opacity-50"
              data-testid="admin-login-submit"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F5EF]">
      {/* Header */}
      <header className="bg-[#3B2F2F] text-white py-4 px-6">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div>
            <h1 className="font-serif text-2xl">Wedding Admin</h1>
            <p className="text-white/60 text-sm">Mary & Arrington | July 31, 2027</p>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={fetchData}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              title="Refresh"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-6">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <Users className="w-6 h-6 text-[#C8A951]" />
                <span className="text-[#5A4A4A] text-sm">Total Responses</span>
              </div>
              <p className="font-serif text-3xl text-[#3B2F2F]">{stats.total_responses}</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <UserCheck className="w-6 h-6 text-green-500" />
                <span className="text-[#5A4A4A] text-sm">Attending</span>
              </div>
              <p className="font-serif text-3xl text-[#3B2F2F]">{stats.attending}</p>
              <p className="text-[#5A4A4A] text-sm">{stats.total_guests} guests</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <UserX className="w-6 h-6 text-red-500" />
                <span className="text-[#5A4A4A] text-sm">Declined</span>
              </div>
              <p className="font-serif text-3xl text-[#3B2F2F]">{stats.not_attending}</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <HelpCircle className="w-6 h-6 text-yellow-500" />
                <span className="text-[#5A4A4A] text-sm">Maybe</span>
              </div>
              <p className="font-serif text-3xl text-[#3B2F2F]">{stats.maybe}</p>
            </div>
          </div>
        )}

        {/* Tabs — wrap to additional rows on narrower desktop / laptop widths
            so the right-side tabs never get cut off. Slightly denser pills
            on <lg screens so more fit per row before wrapping. */}
        <div className="flex flex-wrap gap-2 lg:gap-3 mb-6" data-testid="admin-tabs">
          <button
            onClick={() => setActiveTab('guests')}
            className={`px-4 lg:px-6 py-2 rounded-full font-medium text-sm lg:text-base whitespace-nowrap transition-colors inline-flex items-center gap-2 ${
              activeTab === 'guests'
                ? 'bg-[#3B2F2F] text-white'
                : 'bg-white text-[#3B2F2F] hover:bg-[#E5E0D8]'
            }`}
            data-testid="admin-guests-tab"
          >
            <UserPlus className="w-4 h-4" />
            Guest List
          </button>
          <button
            onClick={() => setActiveTab('rsvps')}
            className={`px-4 lg:px-6 py-2 rounded-full font-medium text-sm lg:text-base whitespace-nowrap transition-colors ${
              activeTab === 'rsvps' 
                ? 'bg-[#3B2F2F] text-white' 
                : 'bg-white text-[#3B2F2F] hover:bg-[#E5E0D8]'
            }`}
          >
            Legacy RSVPs ({rsvps.length})
          </button>
          <button
            onClick={() => setActiveTab('guestbook')}
            className={`px-4 lg:px-6 py-2 rounded-full font-medium text-sm lg:text-base whitespace-nowrap transition-colors ${
              activeTab === 'guestbook' 
                ? 'bg-[#3B2F2F] text-white' 
                : 'bg-white text-[#3B2F2F] hover:bg-[#E5E0D8]'
            }`}
            data-testid="admin-guestbook-tab"
          >
            Guest Book ({guestbookCount})
          </button>
          <button
            onClick={() => setActiveTab('gallery')}
            className={`px-4 lg:px-6 py-2 rounded-full font-medium text-sm lg:text-base whitespace-nowrap transition-colors inline-flex items-center gap-2 ${
              activeTab === 'gallery' 
                ? 'bg-[#3B2F2F] text-white' 
                : 'bg-white text-[#3B2F2F] hover:bg-[#E5E0D8]'
            }`}
            data-testid="admin-gallery-tab"
          >
            <ImageIcon className="w-4 h-4" />
            Gallery
          </button>
          <button
            onClick={() => setActiveTab('hero')}
            className={`px-4 lg:px-6 py-2 rounded-full font-medium text-sm lg:text-base whitespace-nowrap transition-colors inline-flex items-center gap-2 ${
              activeTab === 'hero'
                ? 'bg-[#3B2F2F] text-white'
                : 'bg-white text-[#3B2F2F] hover:bg-[#E5E0D8]'
            }`}
            data-testid="admin-hero-tab"
          >
            <Home className="w-4 h-4" />
            Home Hero
          </button>
          <button
            onClick={() => setActiveTab('event')}
            className={`px-4 lg:px-6 py-2 rounded-full font-medium text-sm lg:text-base whitespace-nowrap transition-colors inline-flex items-center gap-2 ${
              activeTab === 'event'
                ? 'bg-[#3B2F2F] text-white'
                : 'bg-white text-[#3B2F2F] hover:bg-[#E5E0D8]'
            }`}
            data-testid="admin-event-tab"
          >
            <CalendarDays className="w-4 h-4" />
            Event Page
          </button>
          <button
            onClick={() => setActiveTab('story')}
            className={`px-4 lg:px-6 py-2 rounded-full font-medium text-sm lg:text-base whitespace-nowrap transition-colors inline-flex items-center gap-2 ${
              activeTab === 'story'
                ? 'bg-[#3B2F2F] text-white'
                : 'bg-white text-[#3B2F2F] hover:bg-[#E5E0D8]'
            }`}
            data-testid="admin-story-tab"
          >
            <Heart className="w-4 h-4" />
            Our Story
          </button>
          <button
            onClick={() => setActiveTab('event-day')}
            className={`px-4 lg:px-6 py-2 rounded-full font-medium text-sm lg:text-base whitespace-nowrap transition-colors inline-flex items-center gap-2 ${
              activeTab === 'event-day'
                ? 'bg-[#3B2F2F] text-white'
                : 'bg-white text-[#3B2F2F] hover:bg-[#E5E0D8]'
            }`}
            data-testid="admin-event-day-tab"
          >
            <MapPin className="w-4 h-4" />
            Event Day
          </button>
          <button
            onClick={() => setActiveTab('schedule')}
            className={`px-4 lg:px-6 py-2 rounded-full font-medium text-sm lg:text-base whitespace-nowrap transition-colors inline-flex items-center gap-2 ${
              activeTab === 'schedule'
                ? 'bg-[#3B2F2F] text-white'
                : 'bg-white text-[#3B2F2F] hover:bg-[#E5E0D8]'
            }`}
            data-testid="admin-schedule-tab"
          >
            <Clock className="w-4 h-4" />
            Schedule
          </button>
          <button
            onClick={() => setActiveTab('travel')}
            className={`px-4 lg:px-6 py-2 rounded-full font-medium text-sm lg:text-base whitespace-nowrap transition-colors inline-flex items-center gap-2 ${
              activeTab === 'travel'
                ? 'bg-[#3B2F2F] text-white'
                : 'bg-white text-[#3B2F2F] hover:bg-[#E5E0D8]'
            }`}
            data-testid="admin-travel-tab"
          >
            <Plane className="w-4 h-4" />
            Travel
          </button>
          <button
            onClick={() => setActiveTab('save-the-date')}
            className={`px-4 lg:px-6 py-2 rounded-full font-medium text-sm lg:text-base whitespace-nowrap transition-colors inline-flex items-center gap-2 ${
              activeTab === 'save-the-date'
                ? 'bg-[#3B2F2F] text-white'
                : 'bg-white text-[#3B2F2F] hover:bg-[#E5E0D8]'
            }`}
            data-testid="admin-save-the-date-tab"
          >
            <Sparkles className="w-4 h-4" />
            Save the Date
          </button>
          <button
            onClick={() => setActiveTab('qr')}
            className={`px-4 lg:px-6 py-2 rounded-full font-medium text-sm lg:text-base whitespace-nowrap transition-colors inline-flex items-center gap-2 ${
              activeTab === 'qr'
                ? 'bg-[#3B2F2F] text-white'
                : 'bg-white text-[#3B2F2F] hover:bg-[#E5E0D8]'
            }`}
            data-testid="admin-qr-tab"
          >
            <QrCode className="w-4 h-4" />
            QR Code
          </button>
          <button
            onClick={() => setActiveTab('rsvp-settings')}
            className={`px-4 lg:px-6 py-2 rounded-full font-medium text-sm lg:text-base whitespace-nowrap transition-colors inline-flex items-center gap-2 ${
              activeTab === 'rsvp-settings'
                ? 'bg-[#3B2F2F] text-white'
                : 'bg-white text-[#3B2F2F] hover:bg-[#E5E0D8]'
            }`}
            data-testid="admin-rsvp-settings-tab"
          >
            <ClipboardList className="w-4 h-4" />
            Meal Options
          </button>
          <button
            onClick={() => setActiveTab('menu')}
            className={`px-4 lg:px-6 py-2 rounded-full font-medium text-sm lg:text-base whitespace-nowrap transition-colors inline-flex items-center gap-2 ${
              activeTab === 'menu'
                ? 'bg-[#3B2F2F] text-white'
                : 'bg-white text-[#3B2F2F] hover:bg-[#E5E0D8]'
            }`}
            data-testid="admin-menu-tab"
          >
            <ClipboardList className="w-4 h-4" />
            Wedding Menu
          </button>
          <button
            onClick={() => setActiveTab('monogram')}
            className={`px-4 lg:px-6 py-2 rounded-full font-medium text-sm lg:text-base whitespace-nowrap transition-colors inline-flex items-center gap-2 ${
              activeTab === 'monogram'
                ? 'bg-[#3B2F2F] text-white'
                : 'bg-white text-[#3B2F2F] hover:bg-[#E5E0D8]'
            }`}
            data-testid="admin-monogram-tab"
          >
            <Feather className="w-4 h-4" />
            Monogram
          </button>
        </div>

        {/* RSVP Table */}
        {activeTab === 'rsvps' && (() => {
          // Filter locally so the search feels instant. Matches against name,
          // phone, email, and meal preference — case-insensitive, whitespace-
          // tolerant. Kept as an IIFE so no other RSVP state changes.
          const q = rsvpSearch.trim().toLowerCase();
          const filtered = q
            ? rsvps.filter((r) => (
                (r.full_name || '').toLowerCase().includes(q) ||
                (r.phone || '').toLowerCase().includes(q) ||
                (r.email || '').toLowerCase().includes(q) ||
                (r.meal_preference || '').toLowerCase().includes(q)
              ))
            : rsvps;
          return (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="admin-rsvp-panel">
            {/* Search bar */}
            <div className="p-4 border-b border-[#E5E0D8] bg-[#FCFBF7] flex items-center gap-3 flex-wrap">
              <div className="relative flex-1 min-w-[220px] max-w-md">
                <Search className="w-4 h-4 text-[#8A7A7A] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" aria-hidden="true" />
                <input
                  type="search"
                  value={rsvpSearch}
                  onChange={(e) => setRsvpSearch(e.target.value)}
                  placeholder="Search by name, phone, email, or meal…"
                  className="w-full pl-9 pr-9 py-2 rounded-full border border-[#E5E0D8] bg-white text-sm text-[#3B2F2F] placeholder:text-[#8A7A7A] focus:outline-none focus:border-[#C8A951] focus:ring-1 focus:ring-[#C8A951]"
                  aria-label="Search RSVPs"
                  data-testid="admin-rsvp-search-input"
                />
                {rsvpSearch && (
                  <button
                    type="button"
                    onClick={() => setRsvpSearch('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full text-[#8A7A7A] hover:text-[#3B2F2F] hover:bg-[#F8F5EF]"
                    aria-label="Clear search"
                    data-testid="admin-rsvp-search-clear"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              <span className="text-xs text-[#8A7A7A]" aria-live="polite" data-testid="admin-rsvp-count">
                {q
                  ? `${filtered.length} of ${rsvps.length} ${rsvps.length === 1 ? 'guest' : 'guests'} matching “${rsvpSearch.trim()}”`
                  : `${rsvps.length} ${rsvps.length === 1 ? 'guest' : 'guests'} total`}
              </span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#F8F5EF]">
                  <tr>
                    <th className="text-left p-4 text-[#3B2F2F] font-medium">Name</th>
                    <th className="text-left p-4 text-[#3B2F2F] font-medium">Phone</th>
                    <th className="text-left p-4 text-[#3B2F2F] font-medium">Email</th>
                    <th className="text-left p-4 text-[#3B2F2F] font-medium">Status</th>
                    <th className="text-left p-4 text-[#3B2F2F] font-medium">Guests</th>
                    <th className="text-left p-4 text-[#3B2F2F] font-medium">Meal</th>
                    <th className="text-left p-4 text-[#3B2F2F] font-medium">Date</th>
                    <th className="text-left p-4 text-[#3B2F2F] font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((rsvp) => (
                    <tr key={rsvp.id} className="border-t border-[#E5E0D8]" data-testid={`admin-rsvp-row-${rsvp.id}`}>
                      <td className="p-4 text-[#3B2F2F] font-medium">{rsvp.full_name}</td>
                      <td className="p-4 text-[#5A4A4A]">{rsvp.phone}</td>
                      <td className="p-4 text-[#5A4A4A]">{rsvp.email || '-'}</td>
                      <td className="p-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          rsvp.attending === 'yes' 
                            ? 'bg-green-100 text-green-700'
                            : rsvp.attending === 'no'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {rsvp.attending === 'yes' ? 'Attending' : rsvp.attending === 'no' ? 'Declined' : 'Maybe'}
                        </span>
                      </td>
                      <td className="p-4 text-[#5A4A4A]">{rsvp.guest_count}</td>
                      <td className="p-4 text-[#5A4A4A]">{rsvp.meal_preference || '-'}</td>
                      <td className="p-4 text-[#5A4A4A] text-sm">{formatDate(rsvp.submitted_at)}</td>
                      <td className="p-4">
                        <button
                          onClick={() => handleDeleteRsvp(rsvp.id)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan="8" className="p-8 text-center text-[#5A4A4A]" data-testid="admin-rsvp-empty">
                        {q ? (
                          <>
                            No guests match <span className="font-medium text-[#3B2F2F]">“{rsvpSearch.trim()}”</span>.{' '}
                            <button
                              type="button"
                              onClick={() => setRsvpSearch('')}
                              className="underline text-[#C8A951] hover:text-[#B09240]"
                            >
                              Clear search
                            </button>
                          </>
                        ) : 'No RSVPs yet'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          );
        })()}

        {/* Guest Book */}
        {activeTab === 'guestbook' && (
          <AdminGuestBookManager authHeader={getAuthHeader} />
        )}

        {/* Gallery Management */}
        {activeTab === 'gallery' && (
          <AdminGalleryManager authHeader={getAuthHeader} />
        )}

        {/* Save the Date share panel */}
        {activeTab === 'save-the-date' && (
          <AdminSaveTheDate authHeader={getAuthHeader} />
        )}

        {/* Wedding Website QR Code */}
        {activeTab === 'qr' && (
          <AdminWeddingQR authHeader={getAuthHeader} />
        )}

        {/* M&A Monogram Library */}
        {activeTab === 'monogram' && (
          <AdminMonogramLibrary authHeader={getAuthHeader} />
        )}

        {/* EverVow-aligned Guest List CMS */}
        {activeTab === 'guests' && (
          <AdminGuestListManager authHeader={getAuthHeader} />
        )}

        {activeTab === 'hero' && (
          <AdminHeroManager authHeader={getAuthHeader} />
        )}

        {/* Event Page CMS */}
        {activeTab === 'event' && (
          <AdminEventContentManager authHeader={getAuthHeader} />
        )}

        {/* Our Story CMS */}
        {activeTab === 'story' && (
          <AdminOurStoryManager authHeader={getAuthHeader} />
        )}

        {/* Event Day (Wedding Day Details) CMS */}
        {activeTab === 'event-day' && (
          <AdminEventDayManager authHeader={getAuthHeader} />
        )}

        {/* Schedule CMS */}
        {activeTab === 'schedule' && (
          <AdminScheduleManager authHeader={getAuthHeader} />
        )}

        {/* Travel CMS */}
        {activeTab === 'travel' && (
          <AdminTravelManager authHeader={getAuthHeader} />
        )}

        {/* Global Wedding Meal Options */}
        {activeTab === 'rsvp-settings' && (
          <AdminRSVPSettings authHeader={getAuthHeader} />
        )}

        {/* Wedding Menu / Catering */}
        {activeTab === 'menu' && (
          <AdminMenuManager authHeader={getAuthHeader} />
        )}
      </main>
    </div>
  );
};

export default AdminPage;
